/**
 * Linear PCM, the arithmetic half.
 *
 * Pulled out of the hook because this is the part that can actually be wrong
 * in a way nobody hears until it ships: a sign error in the float conversion
 * is distortion, and an off-by-one in the resampler is a click on every
 * chunk boundary. None of it needs an AudioContext, so none of it should be
 * trapped behind one.
 */

/** Base64 (how a chunk crosses the wire) to bytes. */
export function base64ToBytes(base64: string): ArrayBuffer {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

/** Bytes to base64, for the microphone's chunks going out. */
export function bytesToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  // In chunks: `apply` on a 100k-sample array overflows the call stack, and
  // a long utterance produces exactly that.
  const STRIDE = 0x8000
  for (let i = 0; i < bytes.length; i += STRIDE) {
    binary += String.fromCharCode(...bytes.subarray(i, i + STRIDE))
  }
  return btoa(binary)
}

/**
 * Signed 16-bit samples as floats in -1..1.
 *
 * Divided by 32768 in both directions rather than 32767 for positives: the
 * asymmetry is inaudible and the alternative is a branch per sample on the
 * hot path.
 */
export function pcm16ToFloat32(buffer: ArrayBuffer): Float32Array {
  const ints = new Int16Array(buffer)
  const floats = new Float32Array(ints.length)
  for (let i = 0; i < ints.length; i++) floats[i] = ints[i] / 32768
  return floats
}

/** Floats in -1..1 as signed 16-bit, clamped so a hot microphone cannot wrap. */
export function float32ToPcm16(samples: Float32Array): ArrayBuffer {
  const ints = new Int16Array(samples.length)
  for (let i = 0; i < samples.length; i++) {
    const clamped = Math.max(-1, Math.min(1, samples[i]))
    ints[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff
  }
  return ints.buffer
}

/**
 * Resample by linear interpolation.
 *
 * Good enough for speech and cheap, which is the trade worth making on the
 * playback path: a proper windowed-sinc filter is better on music and nobody
 * can hear the difference on a voice at sixteen kilohertz.
 *
 * The last output sample clamps to the final input sample rather than
 * reading past it. Reading past is what produces a click at every chunk
 * boundary, which on a continuous stream is a rattle rather than a click.
 */
export function resample(
  samples: Float32Array,
  fromRate: number,
  toRate: number
): Float32Array {
  if (fromRate === toRate || samples.length === 0) return samples
  const ratio = toRate / fromRate
  const outLength = Math.max(1, Math.ceil(samples.length * ratio))
  const out = new Float32Array(outLength)
  const last = samples.length - 1
  for (let i = 0; i < outLength; i++) {
    const position = i / ratio
    const low = Math.floor(position)
    const high = Math.min(low + 1, last)
    const t = position - low
    out[i] = samples[Math.min(low, last)] * (1 - t) + samples[high] * t
  }
  return out
}
